import React, { useCallback, useMemo } from 'react';
import { useThemeObserver } from '@atlaskit/tokens';
import { parseUrl, EMBEDDED_CONFLUENCE_MODE } from '@atlassian/embedded-confluence';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { EmbeddedPanelProps } from '../../common/types/types';
// eslint-disable-next-line jira/import/no-restricted-paths
import { embeddedPanelEntrypoint } from '../../ui/embedded-panel/entrypoint';
import { useEmbeddedPageTracking } from '../use-embedded-page-tracking';
import { useParentProduct } from '../use-parent-product';

const entryPointParams = {};
export const useEmbeddedPanelEntryPoint = () => {
	// TODO: Get current Confluence Data & embeddedConfluenceSource from react-sweet-state
	// TODO: Need FailedRemoteLink in the store and other necessary information such as hostname, title, etc
	const state = {
		confluenceData: {
			contentUrl:
				'https://corina4.jira-dev.com/wiki/spaces/~5efc53e4deb6ca0baa2a079e/pages/99450892/testttt',
			hostname: '',
			mode: EMBEDDED_CONFLUENCE_MODE.VIEW_MODE,
			title: 'test',
		},
		failedRemoteLink: {
			error: undefined,
		},
		embeddedConfluenceSource: 'test',
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		contentType: 'page' as EmbeddedPanelProps['contentType'],
		displayType: 'panel',
		panel: {
			initialWidth: 0.35,
			isOpen: true,
			wasLeftNavOpen: false,
		},
		modal: {
			isOpen: false,
		},
	};

	const {
		contentType,
		confluenceData: { contentUrl: href, hostname, title, mode },
		embeddedConfluenceSource,
		failedRemoteLink,
	} = state;

	const parentProduct = useParentProduct();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		embeddedPanelEntrypoint,
		entryPointParams,
	);

	const isContentError = !href.length || failedRemoteLink.error;
	const fireAnalyticsEvent = useEmbeddedPageTracking();
	const themeState = useThemeObserver();

	const onContentAction = useCallback(
		(action: string, eventName: string) => {
			fireAnalyticsEvent({
				action,
				eventName,
				embeddedConfluenceSource,
			});
		},
		[fireAnalyticsEvent, embeddedConfluenceSource],
	);

	const onClose = useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			eventName: 'closeEmbeddedConfluencePanel',
			embeddedConfluenceSource,
		});
		entryPointActions.unload();

		// TODO: call close right sidebar
	}, [fireAnalyticsEvent, entryPointActions, embeddedConfluenceSource]);

	const { locale } = useIntl();

	// TODO: set different runtime Props based on contentType
	const runtimeProps = useMemo(
		() => ({
			locale,
			hostname,
			hasFooterLogo: false,
			panelTitle: title,
			navigationPolicy: undefined,
			onClose,
			onContentAction,
			parentProduct,
			spaceKey: parseUrl(href)?.spaceKey,
			themeState,
			url: href,
			mode,
			embeddedConfluenceSource,
			contentId: parseUrl(href)?.contentId,
			contentType,
			allowedFeatures: {
				view: [
					'byline-contributors',
					'byline-extensions',
					'page-comments',
					'page-reactions',
					'inline-comments',
					'non-licensed-share',
				],
			},
		}),
		[
			locale,
			hostname,
			title,
			onClose,
			onContentAction,
			parentProduct,
			href,
			contentType,
			themeState,
			embeddedConfluenceSource,
			mode,
		],
	);

	return !isContentError
		? {
				entryPointActions,
				entryPoint: (
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="epPanelEntrypointContainer"
						packageName="@atlassian/jira-issue-create-confluence-content"
						teamName="confluence-better-together"
						runtimeProps={runtimeProps}
					/>
				),
			}
		: { ref: undefined, entryPoint: null };
};
